import { createTheme, type Theme } from '@mui/material/styles';

export type RenterColorTheme =
  | 'default'
  | 'orangeYellow'
  | 'oliveOrange'
  | 'tealSlate';

interface PaletteShade {
  lighter: string;
  light: string;
  main: string;
  dark: string;
  darker: string;
  contrastText: string;
}

const RENTER_PRIMARY_LIGHT = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#007B55',
  dark: '#005249',
  darker: '#003d2e',
  contrastText: '#FFFFFF',
} as const;

const RENTER_PRIMARY_DARK = {
  ...RENTER_PRIMARY_LIGHT,
  // In dark mode, primary.main stays the same dark green but contrastText
  // must be white (#FFFFFF) — black on #007B55 is only ~2.8:1 (fails WCAG AA).
  contrastText: '#FFFFFF',
} as const;

const SHARED_INFO = {
  lighter: '#89d4fa',
  light: '#0ea2ed',
  main: '#007bb8',
  dark: '#00537d',
  darker: '#003f5e',
  contrastText: '#fff',
} as const;

const SHARED_SUCCESS = {
  lighter: '#9cf0cd',
  light: '#00bf72',
  main: '#00844f',
  dark: '#125c3f',
  darker: '#093624',
  contrastText: '#fff',
} as const;

const SHARED_WARNING = {
  lighter: '#ffee9c',
  light: '#ffe259',
  main: '#ffd300',
  dark: '#8c7400',
  darker: '#695700',
  contrastText: '#000',
} as const;

const SHARED_DANGER = {
  lighter: '#ffd5ad',
  light: '#ffa047',
  main: '#a34f00',
  dark: '#7a3b00',
  darker: '#582a00',
  contrastText: '#fff',
} as const;

const SHARED_ERROR = {
  lighter: '#ff8f8f',
  light: '#ff5c5c',
  main: '#d31f1f',
  dark: '#960d0d',
  darker: '#610000',
  contrastText: '#fff',
} as const;

const ORANGE_YELLOW_PRIMARY = {
  lighter: '#ffc8ad',
  light: '#ff8f57',
  main: '#b84812',
  dark: '#85380f',
  darker: '#5e280b',
  contrastText: '#ffffff',
} as const;

const ORANGE_YELLOW_SECONDARY = {
  lighter: '#ffdd99',
  light: '#ffcb66',
  main: '#9e6d0e',
  dark: '#7d5609',
  darker: '#5c3f07',
  contrastText: '#000',
} as const;

const OLIVE_ORANGE_PRIMARY = {
  lighter: '#d1de74',
  light: '#a3ad5a',
  main: '#6e753d',
  dark: '#4d5419',
  darker: '#3f4525',
  contrastText: '#ffffff',
} as const;

const OLIVE_ORANGE_SECONDARY = {
  lighter: '#ffa980',
  light: '#d96e3b',
  main: '#b84812',
  dark: '#9c3300',
  darker: '#752700',
  contrastText: '#fff',
} as const;

const TEAL_SLATE_PRIMARY = {
  lighter: '#bdd8d2',
  light: '#8cbbb1',
  main: '#498074',
  dark: '#376057',
  darker: '#284640',
  contrastText: '#ffffff',
} as const;

const TEAL_SLATE_SECONDARY = {
  lighter: '#cbd0d6',
  light: '#a5adb7',
  main: '#6c7783',
  dark: '#515962',
  darker: '#142838',
  contrastText: '#ffffff',
} as const;

export interface PatPalette {
  boots: string;
  bootsShadow: string;
  chimney?: string;
  chimneyShadow?: string;
  skinShadow: string;
  lineart: string;
}

export const PAT_PALETTES: Record<RenterColorTheme, PatPalette> = {
  default: {
    boots: SHARED_INFO.main,
    bootsShadow: SHARED_INFO.dark,
    skinShadow: '#c4cdd5',
    lineart: '#062335',
  },
  orangeYellow: {
    boots: SHARED_SUCCESS.light,
    bootsShadow: SHARED_SUCCESS.dark,
    skinShadow: '#c4cdd5',
    lineart: '#3a1a08',
  },
  oliveOrange: {
    boots: OLIVE_ORANGE_SECONDARY.main,
    bootsShadow: OLIVE_ORANGE_SECONDARY.dark,
    chimney: '#dcc9a0',
    chimneyShadow: '#b49a68',
    skinShadow: '#c4be9f',
    lineart: '#1c2618',
  },
  tealSlate: {
    boots: '#2d5a8a',
    bootsShadow: '#1a3d5e',
    skinShadow: '#b8c4cc',
    lineart: '#062335',
  },
};

interface ThemePaletteConfig {
  primary: string;
  secondary: string;
  label: string;
}

export const RENTER_THEME_OPTIONS: Record<
  RenterColorTheme,
  ThemePaletteConfig
> = {
  default: {
    primary: RENTER_PRIMARY_LIGHT.main,
    secondary: '#3366FF',
    label: 'Default',
  },
  orangeYellow: {
    primary: ORANGE_YELLOW_PRIMARY.main,
    secondary: ORANGE_YELLOW_SECONDARY.main,
    label: 'Orange / Yellow',
  },
  oliveOrange: {
    primary: OLIVE_ORANGE_PRIMARY.main,
    secondary: OLIVE_ORANGE_SECONDARY.main,
    label: 'Olive / Orange',
  },
  tealSlate: {
    primary: TEAL_SLATE_PRIMARY.main,
    secondary: TEAL_SLATE_SECONDARY.main,
    label: 'Teal / Slate',
  },
};

export function createRenterTheme(
  baseTheme: Theme,
  colorTheme: RenterColorTheme = 'default'
): Theme {
  const isLight = baseTheme.palette.mode === 'light';

  if (colorTheme === 'default') {
    return createTheme(baseTheme, {
      palette: {
        primary: {
          ...baseTheme.palette.primary,
          ...(isLight ? RENTER_PRIMARY_LIGHT : RENTER_PRIMARY_DARK),
        },
      },
    });
  }

  const PRIMARY_MAP: Record<string, PaletteShade> = {
    orangeYellow: ORANGE_YELLOW_PRIMARY,
    oliveOrange: OLIVE_ORANGE_PRIMARY,
    tealSlate: TEAL_SLATE_PRIMARY,
  };
  const SECONDARY_MAP: Record<string, PaletteShade> = {
    orangeYellow: ORANGE_YELLOW_SECONDARY,
    oliveOrange: OLIVE_ORANGE_SECONDARY,
    tealSlate: TEAL_SLATE_SECONDARY,
  };

  const primary = PRIMARY_MAP[colorTheme];
  const secondary = SECONDARY_MAP[colorTheme];

  return createTheme(baseTheme, {
    palette: {
      primary: { ...baseTheme.palette.primary, ...primary },
      secondary: { ...baseTheme.palette.secondary, ...secondary },
      info: { ...baseTheme.palette.info, ...SHARED_INFO },
      success: { ...baseTheme.palette.success, ...SHARED_SUCCESS },
      warning: { ...baseTheme.palette.warning, ...SHARED_WARNING },
      danger: { ...SHARED_DANGER },
      error: { ...baseTheme.palette.error, ...SHARED_ERROR },
    },
  });
}
